/**
 * API Service for Pinpoint Web App
 *
 * Thin client - all LLM processing happens server-side.
 * Uses direct HTTPS to API worker (bypasses bridge for streaming performance).
 */

import { API_URL } from './config';
import { getAuthToken, clearTokenCache, UserStatus } from './auth';

// ============================================================================
// Types
// ============================================================================

export interface TextChunk {
  text: string;
  index: number;
  textStart: string;
}

export interface CaseMetadata {
  title: string;
  citation: string;
  url: string;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

// ============================================================================
// User Status
// ============================================================================

/**
 * Get user status (trial remaining, subscription status)
 */
export async function getUserStatus(): Promise<UserStatus | null> {
  const token = await getAuthToken(false);
  if (!token) return null;

  try {
    const response = await fetch(`${API_URL}/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.status === 401) {
      clearTokenCache();
      return null;
    }

    if (!response.ok) {
      throw new Error(`Status check failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to get user status:', error);
    return null;
  }
}

// ============================================================================
// Chat Streaming
// ============================================================================

/**
 * Custom error for trial expiration
 */
export class TrialExpiredError extends Error {
  constructor() {
    super('Your free trial has ended. Subscribe to continue using Pinpoint.');
    this.name = 'TrialExpiredError';
  }
}

/**
 * Custom error for anonymous trial expiration (IP-based 5 messages)
 */
export class AnonymousTrialExpiredError extends Error {
  constructor() {
    super('Your free messages have been used. Sign up to continue using Pinpoint.');
    this.name = 'AnonymousTrialExpiredError';
  }
}

export type LegalSource = 'canlii' | 'scholar';

/**
 * Stream chat completion via Cloudflare Worker backend
 * Server handles: query classification, BM25 ranking, prompt building, LLM call
 *
 * This uses direct HTTPS (bypasses bridge) for streaming performance.
 */
export async function* streamChat(
  metadata: CaseMetadata,
  chunks: TextChunk[],
  userMessage: string,
  conversationHistory: ChatMessage[] = [],
  source?: LegalSource
): AsyncGenerator<string, void, unknown> {
  const token = await getAuthToken(false);

  // Build headers - include auth only if token available (anonymous mode otherwise)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  console.log(`[Pinpoint] Sending ${chunks.length} chunks to server${token ? '' : ' (anonymous)'}`);

  const response = await fetch(`${API_URL}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: userMessage,
      chunks,
      metadata,
      conversationHistory,
      source
    })
  });

  if (response.status === 401) {
    // Check if anonymous trial expired
    try {
      const data = await response.clone().json() as { code?: string };
      if (data.code === 'ANONYMOUS_TRIAL_EXPIRED') {
        throw new AnonymousTrialExpiredError();
      }
    } catch (e) {
      if (e instanceof AnonymousTrialExpiredError) throw e;
    }
    if (token) clearTokenCache();
    throw new Error('Session expired. Please sign in again.');
  }

  if (response.status === 402) {
    throw new TrialExpiredError();
  }

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error("You're sending messages too quickly. Please wait a moment.");
    }
    throw new Error('Something went wrong. Please try again.');
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (data === '[DONE]') return;

        try {
          const parsed = JSON.parse(data);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) {
            yield content;
          }
        } catch {
          // Skip invalid JSON
        }
      }
    }
  }
}

// ============================================================================
// Citation Verification (kept client-side - needs full response)
// ============================================================================

/**
 * Extract key terms from a claim for verification
 */
function extractClaimTerms(claim: string): string[] {
  const terms: string[] = [];

  // Extract dollar amounts
  const dollarMatches = claim.match(/\$[\d,]+(?:\.\d+)?/g);
  if (dollarMatches) terms.push(...dollarMatches);

  // Extract plain numbers
  const numberMatches = claim.match(/\b\d{2,}\b/g);
  if (numberMatches) terms.push(...numberMatches);

  // Extract dates
  const dateMatches = claim.match(/\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}\b/gi);
  if (dateMatches) terms.push(...dateMatches);

  // Extract percentages
  const percentMatches = claim.match(/\d+(?:\.\d+)?%/g);
  if (percentMatches) terms.push(...percentMatches);

  // Extract significant words
  const words = claim.match(/\b[A-Za-z]{5,}\b/g);
  if (words) {
    const stopWords = new Set(['which', 'where', 'there', 'their', 'would', 'could', 'should', 'about', 'these', 'those', 'being', 'under', 'after', 'before', 'other', 'through']);
    const filtered = words.filter(w => !stopWords.has(w.toLowerCase()));
    terms.push(...filtered.slice(0, 5));
  }

  return terms;
}

/**
 * Calculate match score between claim terms and chunk text
 */
function calculateMatchScore(terms: string[], chunkText: string): number {
  if (terms.length === 0) return 0;

  const lowerChunk = chunkText.toLowerCase();
  let weightedMatches = 0;

  for (const term of terms) {
    const lowerTerm = term.toLowerCase();
    if (lowerChunk.includes(lowerTerm)) {
      weightedMatches += /^\$|^\d/.test(term) ? 2 : 1;
    }
  }

  const maxWeight = terms.reduce((sum, t) => sum + (/^\$|^\d/.test(t) ? 2 : 1), 0);
  return maxWeight > 0 ? weightedMatches / maxWeight : 0;
}

/**
 * Find the best matching chunk for given claim terms
 */
function findBestMatch(
  terms: string[],
  chunks: TextChunk[],
  excludeIndex?: number
): { index: number; score: number } | null {
  let bestMatch: { index: number; score: number } | null = null;

  for (const chunk of chunks) {
    if (chunk.index === excludeIndex) continue;

    const score = calculateMatchScore(terms, chunk.text);
    if (score > 0 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { index: chunk.index, score };
    }
  }

  return bestMatch;
}

interface CitationVerification {
  original: string;
  corrected: string | null;
  citedIndex: number;
  suggestedIndex?: number;
  reason: 'verified' | 'corrected' | 'removed';
}

/**
 * Verify and correct citations in LLM response
 */
export function verifyCitations(
  response: string,
  chunks: TextChunk[]
): { correctedResponse: string; corrections: CitationVerification[] } {
  const corrections: CitationVerification[] = [];

  const chunkMap = new Map<number, TextChunk>();
  for (const c of chunks) {
    chunkMap.set(c.index, c);
  }

  const citationRegex = /(.{0,100})(\[(\d+)(?:\s*[,\-–]\s*\d+)*\])/gi;

  let correctedResponse = response;
  let offset = 0;

  let match;
  while ((match = citationRegex.exec(response)) !== null) {
    const precedingText = match[1];
    const fullCitation = match[2];
    const firstIndex = parseInt(match[3], 10);

    const claimTerms = extractClaimTerms(precedingText);

    if (claimTerms.length === 0) {
      corrections.push({
        original: fullCitation,
        corrected: fullCitation,
        citedIndex: firstIndex,
        reason: 'verified'
      });
      continue;
    }

    const citedChunk = chunkMap.get(firstIndex);
    const citedScore = citedChunk ? calculateMatchScore(claimTerms, citedChunk.text) : 0;
    const bestMatch = findBestMatch(claimTerms, chunks, firstIndex);

    let replacement: string | null = fullCitation;
    let reason: 'verified' | 'corrected' | 'removed' = 'verified';

    if (citedScore === 0 && bestMatch && bestMatch.score >= 0.8) {
      replacement = `[${bestMatch.index}]`;
      reason = 'corrected';
    } else if (citedScore === 0) {
      replacement = '';
      reason = 'removed';
    }

    corrections.push({
      original: fullCitation,
      corrected: replacement === '' ? null : replacement,
      citedIndex: firstIndex,
      suggestedIndex: bestMatch?.index,
      reason
    });

    if (replacement !== fullCitation) {
      const startPos = match.index + match[1].length + offset;
      const endPos = startPos + fullCitation.length;
      correctedResponse =
        correctedResponse.slice(0, startPos) +
        replacement +
        correctedResponse.slice(endPos);
      offset += replacement.length - fullCitation.length;
    }
  }

  return { correctedResponse, corrections };
}
